import React, { useState, useEffect } from "react";
import "../../../css/Common/Components/ContactUs/ContactUs.css"; // Include your custom styles
import { db } from "../../../backend/firebaseConfig";
import { addDoc, collection } from "firebase/firestore";
import NotificationPopup from "./PopUp";
import InputField from "../DesignSystem/InputField";
import CheckBox from "../DesignSystem/Buttons/CheckBox";
import ButtonYellow from "../DesignSystem/Buttons/ButtonYellow";
import CardContactOptions from "../DesignSystem/Cards/CardContactOptions";
import { ReactComponent as CallUsIcon } from "../../../assets/icons/call-us.svg";
import { ReactComponent as EmailUsIcon } from "../../../assets/icons/email-us.svg";
import { ReactComponent as RequestCallbackIcon } from "../../../assets/icons/request-a-callback.svg";
import { RequestCallback } from "./RequestCallback";
import { useNavigate } from "react-router-dom";
import notifyRenderError from "frontend/Utils/notifyError";

/**
 * ContactUs component renders a contact form for users or advisers to submit their enquiries.
 * Handles form state, validation, submission to Firestore, and email confirmation.
 * Displays success or error notifications and provides contact options.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} [props.isAdviser=false] - Determines if the form is for an adviser or a customer.
 * @returns {JSX.Element} The rendered ContactUs form component.
 *
 * @example
 * <ContactUs isAdviser={true} />
 */
export const ContactUs = ({ isAdviser = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    message: "",
    town: "",
    privacyPolicy: false,
    services: [],
    role: isAdviser ? "Adviser" : "Customer",
  });
  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${today.getFullYear()}`;

  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [isCallbackVisible, setCallbackVisible] = useState(false);

  /**
   * Handles changes to form input fields and updates the form data state accordingly.
   *
   *   The event object from the input field change.
   *
   * Updates the corresponding field in the form data state. If the input type is a checkbox,
   * it stores the checked value; otherwise, it stores the input's value.
   */
  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    // validateForm();
  };

  /**
   * Sends a mail confirmation request to the backend server with the provided form data.
   *
   * @async
   * @function sendMailConfirmation
   * @param {Object} params - The parameters object.
   * @param {Object} params.formData - The form data containing user details.
   * @param {string} params.formData.name - The full name of the user.
   * @param {string} params.formData.email - The email address of the user.
   * @returns {Promise<void>} Resolves when the request is complete.
   *
   * @throws Will log an error to the console if the request fails.
   *
   * @example
   * await sendMailConfirmation({ formData: { name: "John Doe", email: "john@example.com" } });
   */
  const sendMailConfirmation = async ({ formData }) => {
    // console.log(formData.email);
    // console.log(formData.name);
    try {
      const response = await fetch(
        // "http://localhost:5000/send-callback-confirmation",
        "https://rosemount-financial-solution-ifa-ltd.onrender.com/send-callback-confirmation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name.trim().split(" ")[0],
            email: formData.email.trim(),
            isAdviser: isAdviser,
            // phoneNumber: formData.phoneNumber,
            // message: formData.message,
            // town: formData.town,
          }),
        }
      );
      const result = await response.json();
      if (response.ok) {
      } else {
        // console.error("Error:", result);
      }
    } catch (error) {
      try {
        await notifyRenderError(error, "NETWORK_ERROR");
      } catch (e) {}
    }

    // handleClosePopup();
    // setShowPopup(true);
  };

  // Validation Function
  /**
   * Validates the contact form fields and sets error messages for invalid or missing inputs.
   *
   * Validation rules:
   * - Name is always required.
   * - If the user is an adviser:
   *   - Phone number is required.
   *   - Email is required.
   * - If the user is not an adviser:
   *   - Either phone number or email is required.
   *   - If email is provided, it must be valid.
   * - Privacy policy acceptance is required.
   * - Message is required.
   *
   * @returns {Object} An object containing error messages for each invalid field.
   */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors["name"] = "Name is required.";

    if (isAdviser) {
      if (!formData.phoneNumber.trim())
        newErrors["phoneNumber"] = "Phone number is required.";
      if (!formData.email.trim()) newErrors["email"] = "Email is required.";
    } else {
      if (!formData.phoneNumber.trim() && !formData.email.trim()) {
        newErrors["phoneNumber"] = "Phone number or email is required.";
        newErrors["email"] = "Phone number or email is required.";
      } else if (formData.email.trim() && !isValidEmail(formData.email)) {
        newErrors["email"] = "Please enter a valid email address.";
      }
    }
    if (formData.privacyPolicy === false) {
      newErrors["privacyPolicy"] = "Please accept the privacy policy.";
    }
    if (!formData.message.trim()) newErrors["message"] = "Message is required.";
    // if (!isAdviser && !formData.town.trim())
    //   newErrors["town"] = "Town/City is required.";

    setErrors(newErrors);
    return newErrors;
  };

  
  /**
   * Handles the submission of the contact form.
   *
   * - Validates the form data.
   * - If validation fails, prevents submission.
   * - If validation passes:
   *   - Shows a popup to indicate submission is in progress.
   *   - Attempts to add the enquiry to the Firestore "Enquiries" collection.
   *   - Sets a success popup message and sends a confirmation email on success.
   *   - Resets the form fields.
   *   - On error, displays an error popup message.
   */
  const handleSubmit = async (e) => {
    // Prevent default form behavior if needed
    // e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setShowPopup(true); // Show success popup
    try {
      await addDoc(collection(db, "Enquiries"), {
        ...formData,
        createdAt: formattedDate,
      });
      setPopupType("success");
      setPopupMessage(
        "Thanks for submitting your details. One of our financial adviser will contact you within 48 working hours."
      );

      sendMailConfirmation({ formData });

      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        message: "",
        town: "",
        privacyPolicy: false,
        services: [],
        role: isAdviser ? "Adviser" : "Customer",
      });
    } catch (error) {
      // console.error("Error submitting form:", error);
      setPopupType("error");
      setPopupMessage(
        "An error occurred while submitting your details. Please try again."
      );
      setShowPopup(true); // Show error popup
    }
  };

  /**
   * Validates whether the provided string is a valid email address.
   *
   * Uses a regular expression to check if the input matches the general pattern of an email address,
   * including support for quoted strings, domain literals, and standard domain formats.
   *
   * @param {string} email - The email address to validate.
   * @returns {boolean} Returns true if the email is valid, otherwise false.
   */
  const isValidEmail = (email) => {
    const emailRegex =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    // console.log(emailRegex.test(email));
    return emailRegex.test(email);
  };

  /**
   * Closes the popup by setting the showPopup state to false.
   * Typically used as an event handler for closing modal dialogs or popups.
   */
  const handleClosePopup = () => {
    setShowPopup(false); // Close the popup
  };

  /**
   * Handles the opening of the callback form/modal.
   * Sets the state to make the callback component visible.
   *
   * @function
   * @returns {void}
   */
  const handleOpenCallback = () => {
    setCallbackVisible(true);
  };

  /**
   * Handles the closing of the callback modal or component.
   * Sets the callback visibility state to false, effectively hiding the callback UI.
   *
   * @function
   * @returns {void}
   */
  const handleCloseCallback = () => {
    setCallbackVisible(false);
  };

  return (
    <>
      <form className="contact-container" onSubmit={handleSubmit}>
        <div className="contact-us-container">
          <div className="contact-us-text-container">
            <h2>Contact Us</h2>
            <div className="Content">
              Before you supply any personal details to us via the contact us
              page, please read our IFA privacy notice document at the bottom of
              the website. This notice sets out how we will process your
              personal data in line with the General Data Protection
              Regulations. Once you have read the privacy notice, please tick to
              confirm that you have read it and that you agree to Rosemount
              processing your personal information for the purpose of contacting
              you. We will not use the details you provide us in the contact
              form to market to you.
            </div>
          </div>
          <div className="form-container">
            <InputField
              title="Name*"
              placeholder="Name"
              name="name"
              value={formData.name.trim() ? formData.name : ""}
              onChange={handleChange}
              required={errors["name"] ? true : false}
              isTextarea={false}
            />
            <InputField
              title={
                isAdviser
                  ? "Phone Number*"
                  : "Phone Number (Please provide a phone number or email*)"
              }
              placeholder="01234 567 890"
              name="phoneNumber"
              required={errors["phoneNumber"] ? true : false}
              isTextarea={false}
              value={formData.phoneNumber.trim() ? formData.phoneNumber : ""}
              onChange={handleChange}
            />
            <InputField
              title={isAdviser ? "Email*" : "Email"}
              placeholder="name@example.com"
              name="email"
              required={errors["email"] ? true : false}
              value={formData.email.trim() ? formData.email : ""}
              onChange={handleChange}
              isTextarea={false}
            />
            {errors["emailAddress"] ===
              "Please enter a valid email address." && (
              <div className="Error-Message">{errors["emailAddress"]}</div>
            )}
            {!isAdviser && (
              <InputField
                title="Town/City"
                placeholder="Enter Your Town or City"
                name="town"
                // required={errors["town"] ? true : false}
                isTextarea={false}
                value={formData.town.trim() ? formData.town : ""}
                onChange={handleChange}
              />
            )}
            <InputField
              title="Message*"
              placeholder="Message"
              name="message"
              required={errors["message"] ? true : false}
              isTextarea={true}
              value={formData.message.trim() ? formData.message : ""}
              onChange={handleChange}
            />

            <div className="privacy-policy-container">
             
              <CheckBox
                name="privacyPolicy"
                error={
                  errors["privacyPolicy"] && formData.privacyPolicy === false
                    ? true
                    : false
                }
                checked={formData.privacyPolicy}
                onChange={(event) => {
                  setFormData({
                    ...formData,
                    privacyPolicy: event.target.checked,
                  });
                }}
              />

              <div className="Content-SemiBold">
                I have read and understood the{"   "}
                <span
                  className="Content-Bold-Underline"
                  onClick={() => {
                    navigate("/PrivacyPolicy");
                    // console.log("Privacy Policy clicked");
                  }}
                >
                  privacy policy
                </span>
              </div>
            </div>
           
            <ButtonYellow buttonText="SUBMIT" onClick={() => handleSubmit()} />
          </div>
        </div>
        <div className="buttons-layout">
          <CardContactOptions
            title="Call Us:"
            icon={CallUsIcon}
            contactInfo="01256 405460 "
            onClickType="call"
            isAdviser={isAdviser}
          />
          <CardContactOptions
            title="Email Us:"
            icon={EmailUsIcon}
            contactInfo="Info@rosemountfsifa.co.uk"
            onClickType="email"
            isAdviser={isAdviser}
          />
          {!isAdviser && (
            <CardContactOptions
              title="Request a Callback"
              icon={RequestCallbackIcon}
              onClickType="callback"
              onOpenCallback={handleOpenCallback}
            />
          )}

          {isCallbackVisible && (
            <div className="notification-overlay">
              <RequestCallback onClose={() => handleCloseCallback()} />
            </div>
          )}
        </div>
      </form>
      {showPopup && (
        <div className="notification-overlay">
          <NotificationPopup
            message={popupMessage}
            type={popupType}
            onClose={handleClosePopup}
          />
        </div>
      )}
    </>
  );
};
